import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomModal from '../../common/CustomModal';
import CustomButton from '../../../components/CustomButton';
import LabelWithHelp from '../../../components/LabelWithHelp';
import Toggle from '../../../components/ui/Toggle';
import { useEditCodeMutation } from '../../../api/voucher/voucherApiSlice';
import { AdminVoucher } from '../../../api/voucher/voucherApiTypes';
import { endOfDayIso, fieldClass } from './CodeTermsFields';
import { instantToLocalDateInputValue } from '../../../utils/dateTimeUtils';

// Edit an UNUSED promo / goodwill code (#1189). Only the safe fields — value,
// %/fixed, expiry, uses — are editable; the code string and the goodwill
// customer/report link are immutable (and prepaid codes are never edited). The
// field labels/help reuse the create-form i18n so there's one source of truth.


interface Props {
    open: boolean;
    voucher: AdminVoucher | null;
    onClose: () => void;
}

const EditCodeModal = ({ open, voucher, onClose }: Props) => {
    const { t } = useTranslation();
    // Goodwill codes are single-use — no max-uses field here either, to match the
    // create form (Neuland call, 2026-07-08). The limit stays at the stored 1.
    const isGoodwill = voucher?.codeType === 'GOODWILL_COMPENSATION';

    const [isPercentage, setIsPercentage] = useState(true);
    const [value, setValue] = useState('');
    const [validUntil, setValidUntil] = useState('');
    const [maxLimit, setMaxLimit] = useState('');
    // #2248: per-customer cap, alongside the global usage limit.
    const [oncePerCustomer, setOncePerCustomer] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const [editCode, { isLoading }] = useEditCodeMutation();

    // Prefill from the selected voucher each time the modal opens.
    useEffect(() => {
        if (open && voucher) {
            setIsPercentage(voucher.discountIsPercentage);
            setValue(voucher.discountValue != null ? String(voucher.discountValue) : '');
            setValidUntil(instantToLocalDateInputValue(voucher.validUntilDate));
            setMaxLimit(String(voucher.maxLimit ?? 1));
            setOncePerCustomer(voucher.oncePerCustomer === true);
            setFormError(null);
        }
    }, [open, voucher]);

    const today = useMemo(() => {
        const d = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }, []);

    const numValue = Number(value);
    const numLimit = Number(maxLimit);
    const isValid =
        Number.isFinite(numValue) &&
        numValue > 0 &&
        (!isPercentage || numValue <= 100) &&
        validUntil.length > 0 &&
        validUntil >= today &&
        Number.isInteger(numLimit) &&
        numLimit >= 1;

    const close = () => {
        setFormError(null);
        onClose();
    };

    const handleSubmit = async () => {
        setFormError(null);
        if (!voucher || !isValid) {
            setFormError(t('vouchers_codes.create_promo.validation'));
            return;
        }
        try {
            await editCode({
                id: voucher.id,
                data: {
                    discountValue: numValue,
                    discountIsPercentage: isPercentage,
                    validUntilDate: endOfDayIso(validUntil),
                    maxLimit: numLimit,
                    // Only meaningful for promo codes — see the switch below.
                    ...(isGoodwill ? {} : { oncePerCustomer }),
                },
            }).unwrap();
            close();
        } catch (err) {
            const message =
                typeof err === 'object' &&
                err !== null &&
                'data' in err &&
                typeof (err as { data?: { message?: string } }).data?.message === 'string'
                    ? (err as { data: { message: string } }).data.message
                    : t('vouchers_codes.edit.error');
            setFormError(message);
        }
    };

    const typeToggle = (active: boolean, label: string, onClick: () => void) => (
        <button
            type={'button'}
            onClick={onClick}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                active
                    ? 'bg-primary text-text-buttonContrast'
                    : 'border border-foreground/15 text-foreground/60 hover:text-foreground'
            }`}
        >
            {label}
        </button>
    );

    return (
        <CustomModal
            open={open}
            title={t('vouchers_codes.edit.title')}
            onClose={close}
            closeButton={
                <CustomButton title={t('common.cancel')} isFilled={false} onClick={close} />
            }
            saveButton={
                <CustomButton
                    title={t('common.save')}
                    loading={isLoading}
                    disabled={!isValid || isLoading}
                    onClick={handleSubmit}
                />
            }
        >
            <div className={'flex flex-col gap-4'}>
                {voucher?.code && (
                    <p className={'text-sm text-foreground/60'}>
                        {t('vouchers_codes.columns.code')}:{' '}
                        <span className={'font-medium text-foreground'}>{voucher.code}</span>
                    </p>
                )}
                <div>
                    <LabelWithHelp
                        text={t('vouchers_codes.create_promo.discount_type_label')}
                        help={t('vouchers_codes.create_promo.help.discount_type')}
                    />
                    <div className={'flex gap-2 mt-1'}>
                        {typeToggle(
                            isPercentage,
                            t('vouchers_codes.create_promo.type_percentage'),
                            () => setIsPercentage(true),
                        )}
                        {typeToggle(
                            !isPercentage,
                            t('vouchers_codes.create_promo.type_fixed'),
                            () => setIsPercentage(false),
                        )}
                    </div>
                </div>
                <div>
                    <LabelWithHelp
                        text={t('vouchers_codes.create_promo.value_label') + ' *'}
                        help={t('vouchers_codes.create_promo.help.value')}
                    />
                    <input
                        className={fieldClass}
                        type={'number'}
                        min={0}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                    />
                </div>
                <div>
                    <LabelWithHelp
                        text={t('vouchers_codes.create_promo.expiry_label') + ' *'}
                        help={t('vouchers_codes.create_promo.help.expiry')}
                    />
                    <input
                        className={fieldClass}
                        type={'date'}
                        min={today}
                        value={validUntil}
                        onChange={(e) => setValidUntil(e.target.value)}
                    />
                </div>
                {!isGoodwill && (
                    <div>
                        <LabelWithHelp
                            text={t('vouchers_codes.create_promo.max_uses_label') + ' *'}
                            help={t('vouchers_codes.create_promo.help.max_uses')}
                        />
                        <input
                            className={fieldClass}
                            type={'number'}
                            min={1}
                            value={maxLimit}
                            onChange={(e) => setMaxLimit(e.target.value)}
                        />
                    </div>
                )}
                {/* #2248: same switch as the create form, next to the global
                    usage limit. Editing is only offered on unused codes, so
                    turning it on can never contradict a redemption that already
                    happened. */}
                {!isGoodwill && (
                    <Toggle
                        checked={oncePerCustomer}
                        onChange={setOncePerCustomer}
                        label={t('vouchers_codes.create_promo.once_per_customer_label')}
                        description={t('vouchers_codes.create_promo.once_per_customer_help')}
                    />
                )}
                {formError && <p className={'text-sm text-red-500'}>{formError}</p>}
            </div>
        </CustomModal>
    );
};

export default EditCodeModal;
